import { Component, Input, OnInit, signal, OnDestroy } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { RegioesUnidadesService } from '../../../../services/regioes-unidades/regioes-unidades.service';
import { EstadosCidadesService } from '../../../../services/estados-cidades/estados-cidades.service';
import { AuthService } from '../../../../services/auth/auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-cadastrar-endereco',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatGridListModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './cadastrar-endereco.html',
  styleUrls: ['./cadastrar-endereco.scss'],
})
export class CadastrarEndereco implements OnInit, OnDestroy {
  @Input() dadosCompartilhados: any;

  enderecoForm: FormGroup;
  companyDescription = '';
  carregando = false;

  departamentosRegionais: any[] = [];
  regioes: any[] = [];
  unidadesOperacionais: any[] = [];
  cidadeOptionsFiltradas: any[] = [];

  // Novas propriedades para modo edição
  modoEdicao = false;
  estabelecimentoParaEditar: any = null;

  isGestorOuSaude = signal(true);
  private authSubscription: Subscription = new Subscription();

  // Opções para os selects
  ufOptions: any[] = [];
  cidadeOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private snackbarService: SnackbarService,
    private regioesUnidadesService: RegioesUnidadesService,
    private estadosCidadesService: EstadosCidadesService,
    private readonly authService: AuthService
  ) {
    this.enderecoForm = this.fb.group({
      idEmpresa: [''],
      idEstabelecimento: [''],
      criadoPorId: [''],

      // Informações do Endereço
      nomeEndereco: [{ value: '', disabled: false }, [Validators.required]],
      cep: [{ value: '', disabled: false }, [Validators.required]],
      logradouro: [{ value: '', disabled: false }, [Validators.required]],
      numero: [{ value: '', disabled: false }],
      complemento: [{ value: '', disabled: false }],
      ufResidencia: [{ value: '', disabled: false }, [Validators.required]],
      cidadeResidencia: [{ value: '', disabled: false }, [Validators.required]],

      // Atendimento SESI
      departamentoRegional: [
        { value: '', disabled: false },
        [Validators.required],
      ],
      regiao: [{ value: '', disabled: false }, [Validators.required]],
      unidadeOperacional: [
        { value: '', disabled: false },
        [Validators.required],
      ],

      // Contato do Endereço
      nomeContato: [{ value: '', disabled: false }],
      emailContato: [
        { value: '', disabled: false },
        [Validators.required, Validators.email],
      ],
      cargoContato: [{ value: '', disabled: false }],
      telefoneContato: [{ value: '', disabled: false }, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.carregarOpcoesEstadosCidades();
    this.montarDescricaoEmpresa();
    this.carregarOpcoes();

    this.enderecoForm
      .get('idEmpresa')
      ?.setValue(this.dadosCompartilhados?.empresaData?.id);

    this.enderecoForm
      .get('criadoPorId')
      ?.setValue(this.authService.getUsuarioId());

    // Verificar permissões e controlar o estado do formulário
    this.verificarPermissoes();

    // Verificar se está em modo edição
    if (
      this.dadosCompartilhados?.modoEdicaoEstabelecimento &&
      this.dadosCompartilhados?.estabelecimentoParaEditar
    ) {
      this.modoEdicao = true;
      this.estabelecimentoParaEditar =
        this.dadosCompartilhados.estabelecimentoParaEditar;

      this.enderecoForm
        .get('idEstabelecimento')
        ?.setValue(this.estabelecimentoParaEditar.idEstabelecimento);

      this.preencherFormularioEdicao();
    }

    // Observar mudanças no departamento regional para carregar regiões
    this.enderecoForm
      .get('departamentoRegional')
      ?.valueChanges.subscribe((depto) => {
        this.onDepartamentoChange(depto);
      });

    // Observar mudanças na região para carregar unidades
    this.enderecoForm.get('regiao')?.valueChanges.subscribe((regiao) => {
      this.onRegiaoChange(regiao);
    });

    // Observar mudanças na UF para carregar cidades
    this.enderecoForm.get('ufResidencia')?.valueChanges.subscribe((ufId) => {
      this.onUFChange(ufId);
    });
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  private verificarPermissoes(): void {
    const isGestorOuSaude =
      this.authService.isGestor() || this.authService.isSaude();
    this.isGestorOuSaude.set(isGestorOuSaude);

    if (isGestorOuSaude) {
      this.enderecoForm.disable();
    } else {
      this.enderecoForm.enable();
    }
  }

  private carregarOpcoesEstadosCidades(): void {
    this.ufOptions = this.estadosCidadesService.getUfOptions();
    this.cidadeOptions = this.estadosCidadesService.getCidadeOptions();
  }

  private carregarOpcoes(): void {
    this.departamentosRegionais =
      this.regioesUnidadesService.getDepartamentosRegionais();
  }

  private onUFChange(idUF: number): void {
    this.enderecoForm.get('cidadeResidencia')?.setValue('');

    if (idUF) {
      this.cidadeOptionsFiltradas = this.cidadeOptions.filter(
        (cidade) => cidade.id_uf === idUF
      );
    } else {
      this.cidadeOptionsFiltradas = [];
    }
  }

  private onDepartamentoChange(departamento: string): void {
    this.enderecoForm.get('regiao')?.setValue('');
    this.enderecoForm.get('unidadeOperacional')?.setValue('');

    if (departamento) {
      this.regioes =
        this.regioesUnidadesService.getRegioesPorDepartamento(departamento);
    } else {
      this.regioes = [];
    }
    this.unidadesOperacionais = [];
  }

  private onRegiaoChange(regiao: string): void {
    this.enderecoForm.get('unidadeOperacional')?.setValue('');

    if (regiao) {
      const departamento = this.enderecoForm.get('departamentoRegional')?.value;
      this.unidadesOperacionais =
        this.regioesUnidadesService.getUnidadesPorRegiao(departamento, regiao);
    } else {
      this.unidadesOperacionais = [];
    }
  }

  private montarDescricaoEmpresa(): void {
    try {
      if (!this.dadosCompartilhados?.empresaSalva) {
        this.snackbarService.error('Dados da empresa não encontrados');
        return;
      }

      const empresa = this.dadosCompartilhados.empresaSalva;
      const enderecoPrincipal = empresa.enderecos?.[0];

      this.companyDescription = `
        ${empresa.nomeFantasia} - ${empresa.razaoSocial} |
        CNPJ: ${empresa.cnpj} |
        ${enderecoPrincipal?.logradouro || 'Endereço'},
        ${enderecoPrincipal?.cidade || 'Cidade'}/${
        enderecoPrincipal?.estado || 'UF'
      } -
        ${enderecoPrincipal?.cep || 'CEP'}
      `;
    } catch (err) {
      this.snackbarService.error('Erro ao carregar dados da empresa');
    }
  }

  private preencherFormularioEdicao(): void {
    if (!this.estabelecimentoParaEditar) return;

    const estabelecimento = this.estabelecimentoParaEditar;
    const wasDisabled = this.enderecoForm.disabled;

    // 1. Primeiro preencher todos os valores com o formulário habilitado
    this.enderecoForm.enable();

    // 2. Preencher informações principais
    this.enderecoForm.patchValue({
      nomeEndereco: estabelecimento.descricao || '',
      departamentoRegional: estabelecimento.departamentoRegional || '',
      regiao: estabelecimento.regiao || '',
      unidadeOperacional: estabelecimento.unidadeOperacional || '',
    });

    // 3. Carregar as opções para os selects
    if (estabelecimento.departamentoRegional) {
      this.regioes = this.regioesUnidadesService.getRegioesPorDepartamento(
        estabelecimento.departamentoRegional
      );

      if (estabelecimento.regiao) {
        this.unidadesOperacionais =
          this.regioesUnidadesService.getUnidadesPorRegiao(
            estabelecimento.departamentoRegional,
            estabelecimento.regiao
          );
      }
    }

    // 4. Preencher endereço
    if (estabelecimento.enderecos?.length > 0) {
      const endereco = estabelecimento.enderecos[0];
      const { estadoCodigo, municipioCodigo } =
        this.estadosCidadesService.resolverEnderecoBackend({
          estado: endereco.estado,
          cidade: endereco.cidade,
        });

      this.enderecoForm.patchValue({
        cep: endereco.cep || '',
        logradouro: endereco.logradouro || '',
        numero: endereco.numero || '',
        complemento: endereco.complemento || '',
        ufResidencia: estadoCodigo || '',
      });

      if (estadoCodigo) {
        this.onUFChange(estadoCodigo);

        setTimeout(() => {
          this.enderecoForm.patchValue({
            cidadeResidencia: municipioCodigo || '',
          });
        }, 100);
      } else {
        this.enderecoForm.patchValue({
          cidadeResidencia: municipioCodigo || '',
        });
      }
    }

    // 5. Preencher contato
    if (estabelecimento.contatos?.length > 0) {
      const contato = estabelecimento.contatos[0];
      this.enderecoForm.patchValue({
        nomeContato: contato.nomeContato || '',
        emailContato: contato.emailContato || '',
        cargoContato: contato.cargoContato || '',
        telefoneContato: contato.telefoneContato || '',
      });
    }

    if (this.isGestorOuSaude()) {
      this.enderecoForm.disable();
    }
  }

  async salvarEndereco(): Promise<void> {
    if (this.enderecoForm.invalid) {
      this.snackbarService.error(
        'Por favor, preencha todos os campos obrigatórios.'
      );
      return;
    }

    this.carregando = true;
    try {
      const estabelecimentoData = this.getEstabelecimentoData();
      if (this.modoEdicao) {
        if (this.dadosCompartilhados?.onAtualizarEstabelecimento) {
          await this.dadosCompartilhados.onAtualizarEstabelecimento(
            estabelecimentoData
          );
        }
      }

      if (!this.modoEdicao) {
        if (!this.dadosCompartilhados?.empresaSalva?.id) {
          this.snackbarService.error('ID da empresa não disponível');
          return;
        }

        if (this.dadosCompartilhados?.onCadastrarEstabelecimento) {
          await this.dadosCompartilhados.onCadastrarEstabelecimento(
            estabelecimentoData
          );
        }

        this.snackbarService.success('Endereço cadastrado com sucesso!');
        this.voltarParaListagem();
      }
    } catch (error: any) {
      console.error('Erro ao salvar endereço:', error);
      this.snackbarService.error(
        error.response?.data?.message || 'Erro ao salvar endereço'
      );
    } finally {
      this.carregando = false;
    }
  }

  private getEstabelecimentoData(): any {
    const formValue = this.enderecoForm.value;

    // Obter estado e cidade usando o serviço
    const estado = this.estadosCidadesService.getEstadoPorCodigo(
      formValue.ufResidencia
    );
    const municipio = this.estadosCidadesService.getMunicipioPorCodigoIBGE(
      formValue.cidadeResidencia
    );

    console.log(formValue);
    return {
      idEmpresa: formValue.idEmpresa,
      idEstabelecimento: formValue.idEstabelecimento,
      criadoPorId: formValue.criadoPorId,
      descricao: formValue.nomeEndereco,
      departamentoRegional: formValue.departamentoRegional,
      regiao: formValue.regiao,
      unidadeOperacional: formValue.unidadeOperacional, // Já é o nome correto
      enderecos: [
        {
          cep: formValue.cep,
          logradouro: formValue.logradouro,
          numero: formValue.numero,
          complemento: formValue.complemento,
          estado: estado ? estado.uf : '',
          cidade: municipio ? municipio.nome : '',
        },
      ],
      contatos: [
        {
          nome: formValue.nomeContato,
          email: formValue.emailContato,
          cargo: formValue.cargoContato,
          telefone: formValue.telefoneContato,
        },
      ],
    };
  }

  voltarParaListagem(): void {
    if (this.dadosCompartilhados?.changeActiveScreen) {
      this.dadosCompartilhados.changeActiveScreen(
        this.dadosCompartilhados.LISTAR_ESTABELECIMENTOS
      );
    }
  }

  cancelar(): void {
    if (
      this.modoEdicao &&
      this.dadosCompartilhados?.onCancelarEdicaoEstabelecimento
    ) {
      this.dadosCompartilhados.onCancelarEdicaoEstabelecimento();
    } else {
      this.voltarParaListagem();
    }
  }
}
